import { Except } from '@common/types/common.type';
import withFormController, {
  WithFormControllerProps,
} from '@ui/hocs/with-form-controller/with-form-controller.hoc';
import { clsxm, styled } from '@ui/utils/style.util';
import {
  ChangeEvent,
  ComponentProps,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  WheelEvent,
  memo,
  useCallback,
  useId,
  useMemo,
  useState,
} from 'react';
import { FieldValues } from 'react-hook-form';
import Label, { LabelProps } from '../label/label.molecule';

// #region Types
type LeadingTrailingContainerProps = ComponentProps<'div'> & { disabled?: boolean };

export type InputProps<FieldType extends FieldValues> = ComponentProps<'input'> &
  WithFormControllerProps<FieldType> &
  Pick<LabelProps<FieldType>, 'containerProps' | 'containerClassName' | 'label'> & {
    helperText?: string;
    labelClassName?: string;
    labelProps?: Except<
      LabelProps<FieldType>,
      | 'htmlFor'
      | 'hasInput'
      | 'label'
      | 'containerProps'
      | 'containerClassName'
      | 'withFormErrorMessage'
      | 'formErrorControl'
      | 'formErrorName'
      | 'formErrorProps'
    >;
    leadingElement?: ReactNode;
    onEnter?: VoidFunction;
    shouldAllowNegative?: boolean;
    shouldSelectAllOnClick?: boolean;
    trailingElement?: ReactNode;
    withErrorMessageHelper?: boolean;
  };
// #endregion

// #region Sub Components
const LeadingTrailingContainer = styled<LeadingTrailingContainerProps>('div', ({ disabled }) => [
  'absolute top-2 h-6 w-6 flex items-center min-w-fit !mt-0',
  disabled && 'opacity-50',
]);
// #endregion

function BaseInput<FieldType extends FieldValues>({
  className,
  containerClassName,
  containerProps,
  control,
  disabled,
  fieldState,
  formState,
  helperText: passedHelperText,
  id: passedId,
  label,
  labelClassName,
  labelProps,
  leadingElement,
  onChange,
  onClick,
  onEnter,
  onKeyDown,
  shouldAllowNegative = true,
  shouldSelectAllOnClick,
  trailingElement,
  type,
  value,
  withErrorMessageHelper,
  ...rest
}: InputProps<FieldType>) {
  // #region Refs
  const [labelElement, setLabelElement] = useState<HTMLLabelElement | null>(null);
  // #endregion

  // #region Computed Values
  const { error: fieldError, invalid: isInvalid } = fieldState || {};
  const helperText = useMemo(
    () => passedHelperText ?? (withErrorMessageHelper && isInvalid ? fieldError?.message : ''),
    [passedHelperText, fieldError, isInvalid, withErrorMessageHelper],
  );
  const leadingTrailingContainerStyle = useMemo(
    () => ({ top: (labelElement?.clientHeight ?? 0) + 8 }),
    [labelElement],
  );
  const elementId = useId();
  const id = passedId ?? elementId;
  const shouldSelectAllOnClickAndIsNumber = shouldSelectAllOnClick && type === 'number';
  // #endregion

  // #region Handlers
  const handleOnWheel = useCallback(
    (event: WheelEvent<HTMLInputElement>) => {
      if (type !== 'number') return;

      const { currentTarget } = event;

      currentTarget.blur();
      setTimeout(() => currentTarget.focus(), 0);
    },
    [type],
  );
  const handleOnClick = useCallback(
    (event: MouseEvent<HTMLInputElement>) => {
      onClick?.(event);

      if (!shouldSelectAllOnClick) return;

      const { currentTarget } = event;
      currentTarget.setSelectionRange(0, currentTarget.value.length);
    },
    [onClick, shouldSelectAllOnClick],
  );
  const handleOnChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!shouldSelectAllOnClickAndIsNumber || !event.target.value) {
        onChange?.(event);
        return;
      }

      const isNegative = event.target.value.startsWith('-') && shouldAllowNegative;
      // eslint-disable-next-line no-param-reassign -- Need to replace non-numeric characters
      event.target.value = `${isNegative ? '-' : ''}${event.target.value.replace(/[^0-9]/g, '')}`;

      onChange?.(event);
    },
    [onChange, shouldAllowNegative, shouldSelectAllOnClickAndIsNumber],
  );
  const handleOnKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') onEnter?.();
      onKeyDown?.(event);
    },
    [onEnter, onKeyDown],
  );
  // #endregion

  return (
    <Label<FieldType>
      {...labelProps}
      ref={setLabelElement}
      className={clsxm('!mt-0', labelClassName, labelProps?.className)}
      containerClassName={clsxm('relative', containerClassName)}
      containerProps={containerProps}
      hasInput
      htmlFor={id}
      label={label}
    >
      <Label.FormErrorMessage<FieldType>
        className={isInvalid ? '' : 'text-inherit'}
        errorMessage={helperText}
        label={label}
      />
      {leadingElement && (
        <LeadingTrailingContainer
          className="left-3"
          disabled={disabled}
          style={leadingTrailingContainerStyle}
        >
          {leadingElement}
        </LeadingTrailingContainer>
      )}
      <input
        {...rest}
        className={clsxm(
          'h-10 bg-white py-[5px] px-2.5 outline-none rounded-[5px] border border-grey-input-border placeholder-grey overflow-ellipsis transition-colors',
          'focus:border-green-brown',
          'disabled:bg-grey-disabled disabled:border-grey-input-border',
          !label && (!isInvalid || !withErrorMessageHelper || !helperText) && '!mt-0',
          isInvalid && ['bg-red-light border-red-deep'],
          !!leadingElement && 'pl-12',
          !!trailingElement && 'pr-12',
          type === 'number' && 'text-right [&::-webkit-inner-spin-button]:appearance-none',
          className,
        )}
        disabled={disabled}
        id={id}
        onChange={handleOnChange}
        onClick={handleOnClick}
        onKeyDown={handleOnKeyDown}
        onWheel={handleOnWheel}
        type={shouldSelectAllOnClickAndIsNumber ? 'text' : type}
        value={value ?? ''}
      />
      {trailingElement && (
        <LeadingTrailingContainer
          className="right-3 !mt-0"
          disabled={disabled}
          style={leadingTrailingContainerStyle}
        >
          {trailingElement}
        </LeadingTrailingContainer>
      )}
    </Label>
  );
}
BaseInput.displayName = 'Input';

const Input = memo(withFormController(BaseInput)) as typeof BaseInput;

export default Input as typeof BaseInput;
